import { FC, useCallback, useEffect, useMemo } from "react";
import { useToggle } from "react-use";
import { Button, Flex, Modal, Space, Switch, Tooltip, Typography, message } from "@payping/web-components";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { PaymentReportDetailDto } from "@payping/api";
import { ApiVersionEnum, PaymentStatusEnum } from "@payping/api";
import { RHFInputNumber } from "@/shared/components";
import useTranslator from "@/shared/translate/translator";
import { useCreateRefund, useGetServiceConfiguration, useGetUserConfiguration } from "@/refund/use-cases";
import { calcDiffDate, panSeparator } from "@/shared/utils";
import { RefundInfo, RefundOperationStateEnum } from "@/refund/repository/refund/v1/refund-v1-api";
import { EXPIRATION_DIRECT_METHOD, MIN_AMOUNT } from "@/refund/constants/general.constant";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber, numberFormatter } from "@payping/utilities-shared/number";

type Props = {
  details?: PaymentReportDetailDto;
  refundInfo?: RefundInfo;
  loading: boolean;
};

type CreateModalConfigProp = {
  amount?: number;
  pan?: string;
  name?: string;
};

interface FormValues {
  amount: number;
  pan?: string;
}

const CreateRefundAction: FC<Props> = ({ details, refundInfo, loading }) => {
  const { t: generalT } = useTranslator();
  const { t } = useTranslator("refund.createAction");
  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      amount: details?.amount || 0,
      pan: "",
    },
  });
  const getUserConfiguration = useGetUserConfiguration();
  // eslint-disable-next-line react-hooks/incompatible-library
  const amount = watch("amount");

  const serviceConfiguration = useGetServiceConfiguration();
  const createRefund = useCreateRefund();

  // todo: change fee & minAmount base on new status or data
  const [on, toggle] = useToggle(false);
  const fee = getUserConfiguration.data?.data.wageValue ?? 0;
  const minAmount = MIN_AMOUNT;
  const expirationDirectMethod = EXPIRATION_DIRECT_METHOD;

  // todo: unComment this only if you have been implemented retry-refund-action & cancel-refund-action
  const iRefundEnable = useMemo(() => {
    // loading
    if (serviceConfiguration.isLoading || loading || getUserConfiguration.isLoading) return false;
    // not paid
    if (details?.paymentStatus !== PaymentStatusEnum.Succeeded)
      return t("modal.refundDisabledReason.primaryTransactionFailed") as string;
    // minAmount
    if ((details?.amount ?? -1) < minAmount) return t("modal.refundDisabledReason.minAmount") as string;
    // supported payment version
    if (details?.version !== ApiVersionEnum.v_3) return t("modal.refundDisabledReason.oldVersionTransaction") as string;
    // have already refund
    if (
      refundInfo?.state &&
      (refundInfo.state === RefundOperationStateEnum.Created ||
        refundInfo.state === RefundOperationStateEnum.PendingBlockage ||
        refundInfo.state === RefundOperationStateEnum.PreparedToProcess ||
        refundInfo.state === RefundOperationStateEnum.ProcessingRefund ||
        refundInfo.state === RefundOperationStateEnum.RefundSucceeded)
    )
      return t("modal.refundDisabledReason.refunded") as string;
    return true;
  }, [details, getUserConfiguration.isLoading, loading, minAmount, refundInfo, serviceConfiguration.isLoading, t]);

  const isDirectRefund = useMemo(() => {
    if (serviceConfiguration.data?.supportedIpgTypes?.length && details?.ipgType) {
      // refund config speaks PaymentIpgTypeEnum while the report speaks IpgTypeEnum — compare gateway ids numerically
      const supportedIpgType = (serviceConfiguration.data.supportedIpgTypes as number[]).includes(details.ipgType);
      if (supportedIpgType) {
        const diffInDays = calcDiffDate(details.payDate || "");
        if (diffInDays < expirationDirectMethod) return true;
        return false;
      }
      return false;
    }
    return false;
  }, [serviceConfiguration.data, details?.ipgType, details?.payDate, expirationDirectMethod]);

  const openModal = () => {
    if (iRefundEnable) toggle();
  };

  const [check, toggleCheck] = useToggle(true);
  const handleChangeCheck = useCallback(
    (checked: boolean) => {
      toggleCheck(checked);
      if (checked && details?.amount && amount !== details?.amount) setValue("amount", details?.amount);
    },
    [toggleCheck, setValue, amount, details?.amount],
  );

  const checkAmount = useCallback(
    (value: number) => {
      if (value) {
        if (check && details?.amount && value < details?.amount) toggleCheck(false);
        if (!check && details?.amount && value >= details?.amount) toggleCheck(true);
      }
    },
    [check, details?.amount, toggleCheck],
  );
  useEffect(() => {
    checkAmount(amount);
  }, [amount, checkAmount]);

  const [modal, contextHolder] = Modal.useModal();
  const createModalConfig = (prop: CreateModalConfigProp) => ({
    title: t("title"),
    content: (
      <Flex className="tw:w-full" wrap>
        <Flex align="center">
          {t("modal.content1", {
            amount: formatLocalizedNumber({ value: prop.amount ?? 0, options: { maximumFractionDigits: 2 } }),
          })}
          <CuToman width={24} height={24} />
        </Flex>
        <div className="ltr tw:dir-ltr!">{panSeparator(prop.pan || "")}</div>
        <div>{t("modal.content2", { name: prop.name })}</div>
      </Flex>
    ),
    cancelText: generalT("common.cancel"),
    okText: t("modal.okText"),
  });
  const createRefundHandler: SubmitHandler<FormValues> = async (data) => {
    try {
      // const config = createModalConfig({ amount: data.amount, pan: data.pan || details?.cardNo, name: details?.payerName });
      const config = createModalConfig({
        amount: data.amount,
        pan: data.pan || details?.cardNo,
        name: details?.payerName,
      });

      const confirmed = await modal.confirm(config);
      if (confirmed) {
        // await createRefund.mutateAsync({
        //   direct: isDirectRefund,
        //   amount: data.amount,
        //   paymentCode: details?.code,
        //   pan: data.pan,
        // });

        await createRefund.mutateAsync({
          direct: isDirectRefund,
          amount: data.amount,
          paymentCode: details?.code,
        });
        message.success({ content: t("success"), duration: 2, key: "create_refund" });
        toggle();
      }
    } catch {
      //
    }
  };

  return (
    <>
      {iRefundEnable === true ? (
        <Button type="primary" onClick={openModal}>
          {t("title")}
        </Button>
      ) : (
        <Tooltip placement="top" title={typeof iRefundEnable === "string" ? iRefundEnable : undefined}>
          <Button type="primary" disabled loading={serviceConfiguration.isLoading || loading}>
            {t("title")}
          </Button>
        </Tooltip>
      )}

      <Modal
        open={on}
        onOk={handleSubmit(createRefundHandler)}
        onCancel={toggle}
        title={
          <Space className="tw:font-medium">
            {t("modal.title")}
            <Flex align="center">
              {formatLocalizedNumber({ value: details?.amount ?? 0, options: { maximumFractionDigits: 2 } })}
              <CuToman width={24} height={24} />
            </Flex>
          </Space>
        }
        footer={[
          <Button
            key="submit"
            type="primary"
            block
            onClick={handleSubmit(createRefundHandler)}
            disabled={createRefund.isPending}
            loading={createRefund.isPending}
          >
            {t("modal.submit")}
          </Button>,
        ]}
      >
        <Flex vertical className="tw:mt-[24px]! tw:w-full">
          <Flex justify="space-between" className="tw:mb-[24px]!">
            <Typography.Paragraph>{t("modal.check")}</Typography.Paragraph>
            <Switch checked={check} onChange={handleChangeCheck} />
          </Flex>

          <RHFInputNumber
            name="amount"
            // @ts-ignore wrong form type
            control={control}
            rules={{
              required: generalT("common.validations.required", { name: t("modal.amount") }),
              min: {
                value: minAmount,
                message: generalT("common.validations.min", { name: t("modal.amount"), min: minAmount }),
              },
              max: {
                value: details?.amount || minAmount,
                message: generalT("common.validations.max", {
                  name: t("modal.amount"),
                  max: details?.amount,
                }),
              },
            }}
            formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
            parser={(value) => numberFormatter.parse(value)}
            disabled={createRefund.isPending}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            controls={false}
            step={1000}
          />

          {/* {!isDirectRefund && (
            <>
              <Divider className="tw:mt-[4px]" />
              <Typography.Text className="tw:mb-[16px] tw:text-[14px]! tw:font-medium!">
                {t("modal.refundToPan")}
              </Typography.Text>
              <Alert className="tw:mb-4" title={t("modal.notAutomatePanAlert")} type="info" />
              <RHFPanInput
                label={`${t("modal.pan")}:`}
                labelClassName="tw:font-medium!"
                name="pan"
                control={control}
                rules={{
                  required: generalT("common.validations.required", { name: t("modal.pan") }),
                  validate: (value: string) =>
                    panValidation({
                      mask: details?.cardNo || "",
                      value,
                      message: generalT("common.validations.pan"),
                    }),
                }}
                disabled={createRefund.isPending}
              />
            </>
          )} */}

          <Flex align="center">
            <Typography.Text className="tw:text-[14px]!">
              {t("modal.fee", { amount: formatLocalizedNumber({ value: fee, options: { maximumFractionDigits: 2 } }) })}
            </Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Flex>
      </Modal>
      {contextHolder}
    </>
  );
  return "";
};
export default CreateRefundAction;
